import { includes } from 'lodash';
import type { ReactElement } from 'react';
import { useSelector } from 'react-redux';
import { Redirect, useLocation } from 'react-router-dom';

import type { RootState } from 'store';

import { featureFlags } from 'common/featureFlags/featureFlags';
import { AuthUserType, BaseRoles, RoleVariantEnum, UserSubType } from 'common/graphql/types';
import { OfflineManager } from 'common/native-app-support/native.offline.manager';
import { isSponsorUser, SPONSOR_HOME_PATH } from 'common/utils/sponsorUtils';
import { isSecurityQuestionMissing, isSiteRoleBySubType } from 'module/auth/login/login.utils';

const STUDY_ROLES = [
    RoleVariantEnum.StudyBuilder,
    RoleVariantEnum.StudySupport,
    RoleVariantEnum.ClinicalTrialLead,
    RoleVariantEnum.BlindedStudyManager,
    RoleVariantEnum.AdminReadOnly,
];

export const AuthUserLandingPage = (): ReactElement => {
    const auth = useSelector((state: RootState) => state.auth);
    const location = useLocation();
    const { activeUser, activeStudySite } = auth || {};
    const isOktaLogin =
        featureFlags.fxIntegration &&
        activeUser?.userType !== AuthUserType.PATIENT &&
        activeUser?.authenticationMethod === 'OKTA';

    if (isSecurityQuestionMissing(activeUser) && !isOktaLogin) {
        return <Redirect to="/activate" />;
    }

    if (activeUser.userType === AuthUserType.PATIENT) {
        return <Redirect to="/patientDashboard" />;
    }

    if (
        !OfflineManager.getInstance().isPhoneOffline() &&
        activeUser.currentPolicy?.[0] &&
        activeUser.acceptedVersionPP?.toString() !== activeUser.currentPolicy[0].majorVersion.toString() &&
        includes(activeUser.permissions, 'SubscriberPP_Read') // When updating permission here make sure to update in switch user
    ) {
        return <Redirect to="/policy" />;
    }

    if (
        featureFlags.fxIntegration &&
        !auth.activeUser.currentUserSubscriber?.currentRoleId &&
        auth.activeUser.currentUserSubscriber?.roleClaims?.length
    ) {
        return <Redirect to="/selectRoleView" />;
    }

    if (
        isSiteRoleBySubType(activeUser?.subType) ||
        (!featureFlags.fxIntegration && activeUser.subType === UserSubType.PRODUCT_SUPPORT)
    ) {
        if (
            !activeStudySite.activeStudySiteReady &&
            !!activeUser?.userStudies?.length &&
            !includes(location.search, 'studyId')
        ) {
            return <Redirect to="/activestudysite" />;
        }
        const homePath = isSponsorUser(activeUser) ? SPONSOR_HOME_PATH : `/siteDashboard${location.search}`;

        return <Redirect to={homePath} />;
    }

    const isInSiteView = !!auth.activeStudySite?.activeStudy;
    const isStudyRole = includes(STUDY_ROLES, activeUser?.roleVClaim?.sk);
    if (
        (BaseRoles.AdminId === activeUser?.roleBClaim?.sk && !includes(activeUser.permissions, 'Subscriber_Create')) ||
        isStudyRole ||
        (featureFlags.fxIntegration && activeUser?.roleVClaim?.sk === RoleVariantEnum.ProductSupport)
    ) {
        const queryParams = new URLSearchParams(location.search);
        const studyId = queryParams.get('studyId');
        if (studyId) {
            return <Redirect to={`/studydetails/Study.${studyId}`} />;
        }
        if (isInSiteView) {
            return <Redirect to="/siteListing" />;
        }

        return <Redirect to="/study" />;
    }

    // since the subscriber admin and sys admin share the same base role
    if (activeUser?.role === RoleVariantEnum.SystemAdministrator) {
        return <Redirect to="/subscriber" />;
    }

    if (RoleVariantEnum.Librarian === activeUser?.roleVClaim?.sk) {
        return <Redirect to="/activitylibrary" />;
    }

    if (RoleVariantEnum.TranslationRepresentative === activeUser?.roleVClaim?.sk) {
        return <Redirect to="/userprofile/general" />;
    }

    return <Redirect to="/dashboard" />;
};
